<template>
  <div>
    <Banner type="order" />
    <div class="page-container j-container-box-shadow container">
      <div class="title">
        <div class="left-section">
          <div class="back" @click="handleBack">
            <BackArrowIcon class="icon" />
            <span class="icon-label">{{ $t('order.back') }}</span>
          </div>

          <a-divider type="vertical" style="margin: 0 30px; height: 28px" />

          <div class="order-title">
            {{ $t('order.part_logistics_title') }}
          </div>
        </div>

        <div class="action-section" @click="handleDownload">
          <DownloadIcon class="icon" />
          <span class="action-label">{{ $t('order.download') }}</span>
        </div>
      </div>

      <a-divider style="margin: 30px 0" />

      <div class="table-container">
        <a-table
          :pagination="ipagination"
          :columns="columns"
          size="middle"
          row-key="id"
          :loading="loading"
          :data-source="dataSource"
        >
          <template #[`${col}-title`] v-for="col in colTitle" :key="col">
            {{ $t(`order.${col}`) }}
          </template>

          <template #eta="{record}">
            {{ record.eta ? moment(record.eta).format('YYYY-MM-DD') : '' }}
          </template>

          <template #etd="{record}">
            {{ record.etd ? moment(record.etd).format('YYYY-MM-DD') : '' }}
          </template>

          <template #order-no>
            {{ orderNum }}
          </template>

          <template #batch="{ record }">
            {{ record.batch ? $t(record.batch) : '' }}
          </template>

          <template #logisticsNum="{ record }">
            <a @click="goToDetail(record)">{{ record.logisticsNum }}</a>
          </template>

          <template #status="{ record }">
            {{ getStatus(record.logisticsStatus) }}
          </template>

          <template #unit="{ text }">
            {{ text ? $t(text) : '' }}
          </template>
        </a-table>

        <div class="pagination-container">
          <a-pagination
            v-model:current="ipagination.current"
            :page-size-options="ipagination.pageSizeOptions"
            :total="ipagination.total"
            show-size-changer
            v-model:pageSize="ipagination.pageSize"
            @showSizeChange="onShowSizeChange"
            @change="onChange"
            size="small"
            show-quick-jumper
          >
            <template #buildOptionText="props">
              <span
                >{{ props.value }} {{ $t('common.page_per_item_text') }}</span
              >
            </template>
          </a-pagination>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { mapState } from 'vuex';
import moment from 'moment';
import Banner from '@/components/banner/Banner.vue';
import BackArrowIcon from '@/components/icons/BackArrowIcon.vue';
import DownloadIcon from '@/components/icons/DownloadIcon.vue';
import { getLogisiticsListByOrder } from '@/api/apis';
import { downloadFile } from '@/api/request';
import { baseUrl } from '@/utils/constants';

export default defineComponent({
  name: 'PartLogisticsInfo',
  components: {
    Banner,
    BackArrowIcon,
    DownloadIcon
  },
  data() {
    const orderNum = (this.$route.query.orderNo as string) || '';
    return {
      moment,
      loading: false,
      orderNum,
      colTitle: [
        'tracking_no',
        'order_no',
        'batch',
        'unit',
        'quantity',
        'delivery_method',
        'status',
        'etd',
        'eta',
        'remark'
      ],
      columns: [
        {
          dataIndex: 'logisticsNum',
          width: 160,
          slots: {
            customRender: 'logisticsNum',
            title: 'tracking_no-title'
          }
        },
        {
          width: 120,
          slots: { customRender: 'order-no', title: 'order_no-title' }
        },
        {
          dataIndex: 'batch',
          width: 100,
          slots: { customRender: 'batch', title: 'batch-title' }
        },
        {
          dataIndex: 'unit',
          width: 100,
          slots: { customRender: 'unit', title: 'unit-title' }
        },
        {
          dataIndex: 'quantity',
          width: 100,
          slots: {
            title: 'quantity-title'
          }
        },
        {
          dataIndex: 'deliveryMethod',
          width: 140,
          slots: {
            title: 'delivery_method-title'
          }
        },
        {
          dataIndex: 'status',
          width: 100,
          slots: {
            customRender: 'status',
            title: 'status-title'
          }
        },
        {
          dataIndex: 'etd',
          width: 100,
          slots: { customRender: 'etd', title: 'etd-title' }
        },
        {
          dataIndex: 'eta',
          width: 100,
          slots: { customRender: 'eta', title: 'eta-title' }
        },
        {
          width: 100,
          dataIndex: 'remark',
          slots: { title: 'remark-title' }
        }
      ],
      dataSource: [],
      id: 0,
      ipagination: {
        current: 1,
        pageSizeOptions: ['5', '10', '15'],
        total: 0,
        showSizeChanger: true,
        pageSize: 5,
        showQuickJumper: true
        // size: 'large'
      }
    };
  },
  created() {
    const id = +(this.$route.params.id as string);
    if (!isNaN(id) && id > 0) {
      this.id = id;
      this.loadData();
    }
  },
  computed: {
    ...mapState(['lang', 'userInfo'])
  },
  methods: {
    async handleDownload() {
      const paramArr: string[] = [];
      paramArr.push(`customerId=${this.userInfo.id}`);
      paramArr.push(`locale=${this.lang}`);

      await downloadFile(
        `${baseUrl}ureport/excel?_u=db:user_logistics_list_by_order_excel.ureport.xml&orderId=${
          this.id
        }&${paramArr.join('&')}`,
        this.$t('order.part_logistics_title') + this.orderNum + '.xlsx',
        null
      );
    },
    getStatus(status) {
      let res = '';

      if (!isNaN(status) && status !== null && status >= 0) {
        res = this.$t(`logistics_status.${status}`);
      }
      return res;
    },
    async loadData() {
      this.loading = true;

      const res = await getLogisiticsListByOrder({
        pageNo: this.ipagination.current,
        pageSize: this.ipagination.pageSize,
        orderId: this.id
      });

      if (res.success) {
        this.ipagination.total = res.result.total;
        this.dataSource = res.result.records || [];
      }

      this.loading = false;
    },
    onShowSizeChange(current) {
      this.ipagination.current = current;

      this.loadData();
    },
    onChange(page, pageSize) {
      this.ipagination.current = page;
      this.ipagination.pageSize = pageSize;

      this.loadData();
    },
    handleBack() {
      this.$router.push('/order?tab=2');
    },
    goToDetail(record) {
      this.$router.push(`/part-logistics-detail/${record.id}`);
    }
  }
});
</script>

<style lang="scss" scoped>
.container {
  padding: 0 49px;
  background-color: white;

  .title {
    padding: 36px 0 0 0;

    display: flex;
    justify-content: space-between;
    align-items: center;

    .left-section {
      display: flex;
      align-items: center;

      .order-title {
        color: $primary-color;
        font-weight: bold;
        font-size: $secondary-fontsize;
      }

      .back {
        display: flex;
        align-items: center;

        cursor: pointer;

        .icon-label {
          margin-left: 10px;
          font-size: $content-fontsize;
          color: #999999;
        }
      }
    }

    .action-section {
      display: flex;
      align-items: center;
      cursor: pointer;
      color: $primary-color;
      .action-label {
        margin-left: 10px;
      }

      &:hover {
        color: $primary-hover-color;
      }
    }
  }

  .table-container {
    margin-bottom: 40px;
  }
}

.pagination-container {
  margin: 20px 0;
  text-align: right;
}

.multi-item {
  margin-bottom: 8px;
}

// 隐藏table自带的pagination
::v-deep(.ant-table-pagination) {
  display: none;
}
</style>
